import { ChevronDown, ArrowUp, ArrowDown, Check } from "lucide-react";
import UserAvatar from "@/components/ui/UserAvatar";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

/**
 * ColumnHeaderMenu — composant partagé pour tri/filtre depuis un en-tête
 * de colonne.
 *
 * Spec 2026-02-XX rev10 (Feb 2026) — support du filtre MULTI-sélection via
 * la prop ``multi``. Quand ``multi=true`` :
 *   • ``current`` est un ARRAY de valeurs sélectionnées (au lieu d'une string).
 *   • cliquer un item TOGGLE sa présence dans la sélection (add si absent,
 *     remove si présent). Le menu ne se ferme PAS après chaque clic (via
 *     ``e.preventDefault()`` sur le DropdownMenuItem) — permet des choix
 *     multiples rapides.
 *   • le badge visuel indique combien d'items sont cochés.
 *   • « Tous » réinitialise la sélection ([]).
 * Quand ``multi=false`` (défaut, backward-compat), comportement single-value
 * inchangé (miroir de ce que Tâches utilise actuellement).
 */
export default function ColumnHeaderMenu({
  label, options, current, onSort, onFilter,
  sortActive, sortDir, testId, accent = "#F9BC26",
  multi = false,
  // Spec iter213 (2026-02-28) — Override du label du bloc filtre principal
  // (ex. « Filtrer par utilisateur » sur les colonnes CRÉÉ LE / MODIFS /
  // RESPO). Défaut : « Filtrer » ou « Filtrer (multi) » selon `multi`.
  filterLabel,
  // Spec rev102 (2026-02) — support d'un SECOND bloc de filtre (utile
  // quand une colonne cumule un filtre "valeur" (ex : liste d'utilisateurs
  // assignés) et un filtre "état" binaire (ex : Assignée / Non assignée).
  // Le bloc secondaire est TOUJOURS multi-select et rendu APRÈS le bloc
  // principal, avec son propre séparateur/libellé.
  secondaryLabel,
  secondaryOptions,
  secondaryCurrent,
  onSecondaryFilter,
}) {
  // Normalise la sélection courante pour un accès uniforme.
  const currentSet = multi
    ? new Set(Array.isArray(current) ? current : [])
    : null;
  const hasSelection = multi ? currentSet.size > 0 : Boolean(current);
  const isChecked = (v) => multi ? currentSet.has(v) : current === v;

  // Bloc secondaire — toujours multi (see spec rev102).
  const secondarySet = new Set(Array.isArray(secondaryCurrent) ? secondaryCurrent : []);
  const hasSecondary = secondarySet.size > 0;
  const totalActive = (multi ? currentSet.size : (hasSelection ? 1 : 0)) + secondarySet.size;

  const handleItemClick = (e, value) => {
    if (!multi) {
      onFilter(value);
      return;
    }
    // Empêche Radix de fermer le menu — utile pour cocher plusieurs items
    // à la suite sans devoir rouvrir le dropdown.
    e.preventDefault();
    const next = new Set(currentSet);
    if (next.has(value)) next.delete(value); else next.add(value);
    onFilter(Array.from(next));
  };

  // Handler du bloc secondaire — même logique multi-toggle, sans fermeture.
  const handleSecondaryClick = (e, value) => {
    e.preventDefault();
    if (!onSecondaryFilter) return;
    const next = new Set(secondarySet);
    if (next.has(value)) next.delete(value); else next.add(value);
    onSecondaryFilter(Array.from(next));
  };

  return (
    <div className="inline-flex items-center gap-1 whitespace-nowrap">
      <span className="truncate">{label}</span>
      {sortActive && (sortDir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            className="ml-0.5 p-0.5 rounded hover:bg-white/10 relative"
            aria-label={`Filtrer ${label || "colonne"}`}
            data-testid={`col-menu-${testId}`}
            style={{ color: accent }}
          >
            <ChevronDown size={14} strokeWidth={2.5} />
            {(multi && currentSet.size > 0) || hasSecondary ? (
              <span
                className="absolute -top-1 -right-1 text-[9px] font-bold rounded-full px-1 leading-[13px]"
                style={{ background: accent, color: "#1B1B1B", minWidth: 13, textAlign: "center" }}
              >
                {totalActive}
              </span>
            ) : null}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[200px]" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Trier
          </DropdownMenuLabel>
          <DropdownMenuItem onClick={() => onSort("asc")} data-testid={`col-sort-asc-${testId}`}>
            <ArrowUp size={12} className="mr-2" /> Croissant
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onSort("desc")} data-testid={`col-sort-desc-${testId}`}>
            <ArrowDown size={12} className="mr-2" /> Décroissant
          </DropdownMenuItem>
          {options && options.length > 0 && onFilter && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {filterLabel || (multi ? "Filtrer (multi)" : "Filtrer")}
              </DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => onFilter(multi ? [] : "")}
                data-testid={`col-filter-all-${testId}`}
              >
                <span className="w-3.5 mr-1.5">{!hasSelection && <Check size={12} />}</span>
                <span className="text-muted-foreground">Tous</span>
              </DropdownMenuItem>
              {options.map((o) => (
                <DropdownMenuItem
                  key={o.value}
                  onSelect={(e) => handleItemClick(e, o.value)}
                  data-testid={`col-filter-${testId}-${o.value}`}
                >
                  <span className="w-3.5 mr-1.5">{isChecked(o.value) && <Check size={12} />}</span>
                  {o.avatar ? (
                    // Spec iter213 (2026-02-28) — mode « filtre utilisateur »
                    // : pastille avatar (initiales + couleur déterministe
                    // via avatarColorForSeed) au lieu du pastille de statut.
                    o.avatar.user ? (
                      <span className="inline-flex rounded-full mr-2 shrink-0"><UserAvatar user={o.avatar.user} size={18} showPopover={false} /></span>
                    ) : (
                    <span
                      className="inline-flex items-center justify-center rounded-full mr-2 shrink-0 font-bold"
                      style={{
                        width: 18, height: 18, fontSize: 8.5,
                        background: o.avatar.bg,
                        color: o.avatar.fg,
                        letterSpacing: "0.02em",
                      }}
                    >
                      {o.avatar.initials}
                    </span>
                    )
                  ) : (o.color && (
                    <span
                      className="inline-block w-2 h-2 rounded-full mr-1.5"
                      style={{ background: o.color }}
                    />
                  ))}
                  {o.label}
                </DropdownMenuItem>
              ))}
            </>
          )}
          {secondaryOptions && secondaryOptions.length > 0 && onSecondaryFilter && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {secondaryLabel || "Filtrer (multi)"}
              </DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => onSecondaryFilter([])}
                data-testid={`col-filter2-all-${testId}`}
              >
                <span className="w-3.5 mr-1.5">{!hasSecondary && <Check size={12} />}</span>
                <span className="text-muted-foreground">Tous</span>
              </DropdownMenuItem>
              {secondaryOptions.map((o) => (
                <DropdownMenuItem
                  key={o.value}
                  onSelect={(e) => handleSecondaryClick(e, o.value)}
                  data-testid={`col-filter2-${testId}-${o.value}`}
                >
                  <span className="w-3.5 mr-1.5">{secondarySet.has(o.value) && <Check size={12} />}</span>
                  {o.color && (
                    <span
                      className="inline-block w-2 h-2 rounded-full mr-1.5"
                      style={{ background: o.color }}
                    />
                  )}
                  {o.label}
                </DropdownMenuItem>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
